import { Box, Grid, Typography } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useContext, useEffect, useState } from "react";
import { Controller, FieldValues, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useAppDispatch } from "../../../app/hooks";
import CommonTextField from "../../../common/components/functional/commonTextField/CommonTextField";
import { Talent } from "../../talents/talentAPI";
import { updateEezyReview } from "../../talents/talentsSlice";
import { AccessRightContext } from "../../user/AccessRightProvider";
import TalentHrActionButtons from "./TalentHrActionButtons";

type Props = {
  talent: Talent;
  disableEdit?: boolean;
};

const TalentHrEezyReview = ({ talent, disableEdit = false }: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("talentProfile");
  const { canEditTalentProfile } = useContext(AccessRightContext);

  const MAX_REVIEW_LENGTH = 5000;
  const [isEditing, setIsEditing] = useState(false);

  const defaultValues = { eezyReview: talent?.eezyReview?.value };

  const {
    control,
    watch,
    reset,
    formState: { isDirty, isValid },
    handleSubmit,
  } = useForm({
    defaultValues,
    mode: "onChange",
  });

  useEffect(() => {
    reset({ eezyReview: talent?.eezyReview?.value });
  }, [talent?.eezyReview?.value, reset]);

  const eezyReview = watch("eezyReview");
  const showReviewVisibilityLabel = eezyReview || isEditing;
  const saveDisabled = !isValid || !isDirty;

  const onEdit = () => setIsEditing(true);

  const onSave = (values: FieldValues) => {
    dispatch(
      updateEezyReview({
        talentId: talent.id,
        eezyReview: values.eezyReview,
        version: talent?.eezyReview?.version || 0,
      }),
    ).then((action) => {
      if (isFulfilled(action)) {
        setIsEditing(false);
      }
    });
  };

  const onCancel = () => {
    reset(defaultValues);
    setIsEditing(false);
  };

  return (
    <>
      <Grid container alignItems="center" columnGap={2} pt="14px" pb="4px">
        <Grid item width="102px" alignSelf="start" mt="5px">
          <Typography
            textTransform="uppercase"
            fontWeight="700"
            fontSize="14px"
            variant="header"
          >
            {t("eezyReview")}
          </Typography>
        </Grid>
        <Grid item container xs>
          <Grid item xs={12}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap="16px"
            >
              <Grid item xs={9}>
                {isEditing ? (
                  <Controller
                    name="eezyReview"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <CommonTextField
                        {...field}
                        fullWidth
                        sx={{ maxWidth: "900px" }}
                        size="small"
                        multiline
                        inputProps={{ maxLength: MAX_REVIEW_LENGTH }}
                      />
                    )}
                  />
                ) : (
                  <Typography style={{ wordWrap: "break-word" }}>
                    {eezyReview || t("reviewMissing")}
                  </Typography>
                )}
                <Box display="flex" justifyContent="space-between" mt="8px">
                  {showReviewVisibilityLabel && (
                    <Typography variant="secondary" fontSize="12px">
                      {t("reviewVisibility")}
                    </Typography>
                  )}
                  {isEditing && (
                    <Typography variant="secondary" fontSize="12px">{`${
                      eezyReview?.length || 0
                    }/${MAX_REVIEW_LENGTH}`}</Typography>
                  )}
                </Box>
              </Grid>
              {canEditTalentProfile && !disableEdit && (
                <TalentHrActionButtons
                  isEditing={isEditing}
                  onEdit={onEdit}
                  onSave={handleSubmit(onSave)}
                  onCancel={onCancel}
                  saveDisabled={saveDisabled}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default TalentHrEezyReview;
